<template>
  <AppHeader />
  <div class="min-h-screen bg-gradient-to-br from-primary-lighter via-white to-primary-lighter pt-8 pb-20 px-4">
    <div class="max-w-7xl mx-auto space-y-10">
      <!-- Header Section -->
      <div class="text-center mb-8">
        <h1 class="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
          Choose Your <span class="text-primary">Perfect Plan</span>
        </h1>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Unlock more video ideas and grow your channel faster. Cancel anytime.
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="!globalSettings || !globalSettings.plans" class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div v-for="n in 3" :key="n" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center">
          <div class="animate-pulse w-full">
            <div class="h-8 w-8 bg-primary-red-light rounded-full mb-4 mx-auto"></div>
            <div class="h-4 bg-primary-red-light rounded w-3/4 mb-2 mx-auto"></div>
            <div class="h-4 bg-primary-red-light rounded w-1/2 mx-auto"></div>
            <div class="h-10 bg-primary-red-light rounded w-full mt-6"></div>
          </div>
        </div>
      </div>

      <!-- Plans Display -->
      <div v-else>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          <template v-for="plan in visiblePlans" :key="plan.id || plan.name">
            <div
              class="rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-2xl flex flex-col relative"
              :class=" [
                plan.mostPopular 
                  ? 'border-primary bg-gradient-to-br from-primary to-primary-dark' 
                  : 'bg-white border-gray-200 hover:border-primary'
              ]"
            >
              <!-- Most Popular Badge -->
              <div v-if="plan.mostPopular" class="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                <span class="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-xs font-bold shadow-lg">
                  MOST POPULAR
                </span>
              </div>

              <div class="p-8 flex-1 flex flex-col">
                <!-- Plan Name -->
                <h3
                  class="text-2xl font-bold mb-2"
                  :class="plan.mostPopular ? 'text-white' : 'text-gray-900'"
                >
                  {{ plan.name }}
                </h3>
                
                <!-- Plan Description -->
                <p
                  class="mb-4 font-medium"
                  :class="plan.mostPopular ? 'text-white/90' : 'text-red-600'"
                >
                  {{ plan.description }}
                </p>
                
                <!-- Price & Button Section -->
                <div class="border rounded-lg p-4 mb-6 flex flex-col items-start"
                  :class="plan.mostPopular ? 'bg-white/10 border-white/20' : 'bg-gray-50 border-gray-200'"
                >
                  <div class="mb-4">
                    <div
                      class="text-4xl font-extrabold"
                      :class="plan.mostPopular ? 'text-white' : 'text-gray-900'"
                    >
                      ${{ plan.price || 0 }}
                    </div>
                    <div
                      class="text-sm mt-1"
                      :class="plan.mostPopular ? 'text-white/80' : 'text-gray-500'"
                    >
                      per month
                    </div>
                  </div>
                  
                  <button
                    @click="handleSelectPlan(plan.name)"
                    :class=" [
                      'w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200',
                      plan.mostPopular
                        ? 'bg-white text-primary hover:bg-gray-100'
                        : plan.name.toLowerCase().includes('starter')
                          ? 'bg-primary text-white hover:bg-primary-dark'
                          : 'text-primary border-2 border-primary hover:bg-primary hover:text-white'
                    ]"
                  >
                    {{ plan.name.toLowerCase().includes('free') ? 'Get Started Free' : plan.name.toLowerCase().includes('pro') ? 'Get Pro Access' : 'Get Started' }}
                  </button>
                </div>

                <!-- Features List -->
                <ul class="space-y-3 mb-8 flex-1">
                  <li v-for="feature in plan.features" :key="feature" class="flex items-start">
                    <span
                      class="material-symbols-outlined mr-2 text-xl"
                      :class="plan.mostPopular ? 'text-yellow-400' : 'text-green-500'"
                    >
                      check_circle
                    </span>
                    <span :class="plan.mostPopular ? 'text-white' : 'text-gray-700'">
                      {{ feature }}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <section class="pt-20 pb-20 px-4">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-3xl font-bold text-center text-gray-900 mb-12">
          Frequently Asked Questions
        </h2>
        <div class="space-y-4">
          <div 
            v-for="(faq, index) in faqs" 
            :key="index"
            class="bg-white rounded-lg shadow-sm border border-gray-200 transition overflow-hidden"
          >
            <button
              @click="toggleFaq(index)"
              class="w-full px-6 py-4 text-left bg-white hover:bg-primary/5 focus:outline-none focus:bg-primary/5 transition-colors duration-200 rounded-t-lg"
              :class="{ 'bg-primary/5': openFaqs.includes(index) }"
            >
              <div class="flex items-center justify-between">
                <h3 class="text-base md:text-lg font-semibold text-primary-dark">{{ faq.question }}</h3>
                <span 
                  class="material-symbols-outlined text-primary transition-transform duration-200"
                  :class="{ 'rotate-180': openFaqs.includes(index) }"
                >
                  expand_more
                </span>
              </div>
            </button>
            <div 
              v-show="openFaqs.includes(index)"
              class="px-6 pb-4 text-sm text-gray-700 leading-relaxed bg-primary/5 rounded-b-lg"
            >
              <span v-if="faq.answer.includes('tyteapps@gmail.com')">
                {{ faq.answer.split('tyteapps@gmail.com')[0] }}
                <a href="mailto:tyteapps@gmail.com" class="text-primary hover:underline">tyteapps@gmail.com</a>
                {{ faq.answer.split('tyteapps@gmail.com')[1] }}
              </span>
              <span v-else>{{ faq.answer }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
  <AppFooter />
</template>

<script>
import { mapState } from 'vuex';
import AppHeader from '@/components/common/AppHeader.vue';
import AppFooter from '@/components/common/AppFooter.vue';

export default {
  name: 'PricingPage',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      openFaqs: [],
      faqs: [
        {
          question: "Can I change plans later?",
          answer: "Yes! You can upgrade or downgrade your plan at any time from your account settings."
        },
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards, debit cards, and PayPal through our secure payment processor."
        },
        {
          question: "Is there a free trial?",
          answer: "Yes! The Free Plan gives you access to basic features with 3 generations per month, no credit card required."
        },
        {
          question: "Can I cancel anytime?",
          answer: "Yes! You can cancel your subscription at any time from your account settings. There are no long-term commitments or cancellation fees."
        },
        {
          question: "How can I quickly contact TrendzTube team?",
          answer: "You can reach us directly at tyteapps@gmail.com for any questions, support requests, or feedback. We aim to respond within 24 hours."
        }
      ]
    };
  },
  computed: {
    ...mapState(['user', 'globalSettings']),
    visiblePlans() {
      // Use globalSettings.plans and preserve order by position
      const plans = this.globalSettings?.plans || {};
      return Object.values(plans)
        .filter(plan => plan && plan.visible)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
  },
  methods: {
    toggleFaq(index) {
      if (this.openFaqs.includes(index)) {
        this.openFaqs = this.openFaqs.filter(i => i !== index);
      } else {
        this.openFaqs.push(index);
      }
    },
    handleSelectPlan() {
      if (!this.user) {
        this.$router.push('/login');
        return;
      }
      this.$router.push('/upgrade');
    }
  }
};
</script>

<style scoped>
.shadow-lg {
  box-shadow: 0 10px 25px -5px rgba(80, 36, 255, 0.1), 0 10px 10px -5px rgba(80, 36, 255, 0.04);
}
.shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(80, 36, 255, 0.25);
}
.rounded-lg {
  border-radius: 0.75rem;
}
.rounded-2xl {
  border-radius: 1rem;
}
.border-gray-200 {
  --tw-border-opacity: 1;
  border-color: rgb(229 231 235 / var(--tw-border-opacity));
}
.bg-white {
  --tw-bg-opacity: 1;
  background-color: rgb(255 255 255 / var(--tw-bg-opacity));
}
.bg-gray-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(243 244 246 / var(--tw-bg-opacity));
}
.text-primary-red {
  color: #e11d48;
}
.bg-primary-red {
  background-color: #e11d48;
}
.bg-primary-red-dark {
  background-color: #be123c;
}
.bg-primary-red-light {
  background-color: #fee2e2;
}
.hover\:bg-primary-red-dark:hover {
  background-color: #be123c;
}
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
